import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { billingApi } from '@/api/ops';
import type { CostsSummary } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { BarList } from '@/components/BarList';
import { TrendChart } from '@/components/TrendChart';
import { PageHeader, Section } from '@/components/ops/Layout';
import { EnvironmentPicker, MonthSelect } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { QueryState } from '@/components/ops/Problem';
import { formatCredits, microToDisplayed } from '@/ops/credits';
import { currentPeriod, formatBps, formatNumber, formatPeriod } from '@/ops/format';
import { LIVE } from '@/ops/live';
import { useSelectedEnvironment } from '@/ops/useEnvironment';

/**
 * Platform costs for one environment and month (plan §6.2): totals by product, margins, the top tenants
 * and the month trend — from billing's own aggregate (`costs/summary`), never by iterating tenants.
 * Live; billing down is the labelled 503 panel.
 */
export function CostsPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  const [period, setPeriod] = useState(currentPeriod());
  const summary = useQuery({
    queryKey: ['ops', 'billing', env, 'costs-summary', period],
    queryFn: () => billingApi.costsSummary(env!, period),
    enabled: !!env,
    ...LIVE,
  });

  return (
    <RequirePermission permission={PERMISSIONS.costsRead} what="Costs">
      <PageHeader
        title="Costs"
        subtitle="What tenants consumed (credits), what it cost us, and the margin — live from billing."
        actions={
          <>
            <EnvironmentPicker env={env} environments={environments} onChange={setEnv} />
            <MonthSelect value={period} onChange={setPeriod} />
          </>
        }
      />
      {env && (
        <QueryState query={summary} service="billing">
          {(data) => <CostsBody env={env} data={data} />}
        </QueryState>
      )}
    </RequirePermission>
  );
}

function CostsBody({ env, data }: { env: string; data: CostsSummary }) {
  const scale = data.credit_display_scale;
  const t = data.totals;
  const trend = [...data.trend].sort((a, b) => a.period.localeCompare(b.period));

  return (
    <>
      <div className="kpi-row">
        <Tile label="Consumed (credits)" value={formatCredits(t.credits, scale)} sub={`${formatNumber(t.events)} rated events`} />
        <Tile label="Our cost" value={formatCredits(t.cost_credits, scale)} sub="provider bills, in credits" />
        <Tile label="Margin" value={formatCredits(t.margin_credits, scale)} sub={`${formatBps(t.margin_bps)} of consumption`} />
      </div>

      <Section title="By product" meta={formatPeriod(data.period)} flush>
        {data.products.length === 0 ? (
          <div className="empty-state t-body">No rated usage in {data.period}.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Product</th><th className="num">Credits</th><th className="num">Our cost</th><th className="num">Margin</th><th className="num">Margin %</th><th className="num">Events</th></tr></thead>
              <tbody>
                {data.products.map((p) => (
                  <tr key={p.product}>
                    <td className="mono">{p.product}</td>
                    <td className="num">{formatCredits(p.credits, scale)}</td>
                    <td className="num">{formatCredits(p.cost_credits, scale)}</td>
                    <td className="num">{formatCredits(p.margin_credits, scale)}</td>
                    <td className="num">{formatBps(p.margin_bps)}</td>
                    <td className="num">{formatNumber(p.events)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Stacked, not side by side: the trend chart scales its text with its box, so it needs the full width. */}
      <div className="stack">
        <Section title="Top tenants" meta="by credits consumed">
          <BarList
            rows={data.top_tenants.map((tt) => ({
              label: tt.name ?? tt.tenant_id,
              // Displayed credits for the bar length; the table below carries the exact figures.
              value: microToDisplayed(tt.credits, scale) ?? tt.credits,
            }))}
            limit={10}
            emptyLabel="No tenant consumed anything this month."
          />
          {data.top_tenants.length > 0 && (
            <details className="viz-table">
              <summary>Table</summary>
              <table className="table">
                <thead><tr><th>Tenant</th><th className="num">Credits</th><th className="num">Cost</th><th className="num">Margin</th></tr></thead>
                <tbody>
                  {data.top_tenants.map((tt) => (
                    <tr key={tt.tenant_id}>
                      <td><Link to={`/ops/tenants/${encodeURIComponent(env)}/${tt.tenant_id}?tab=costs`}>{tt.name ?? tt.tenant_id}</Link></td>
                      <td className="num">{formatCredits(tt.credits, scale)}</td>
                      <td className="num">{formatCredits(tt.cost_credits, scale)}</td>
                      <td className="num">{formatCredits(tt.margin_credits, scale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </Section>

        <Section title="Month trend" meta="consumption vs our cost, displayed credits">
          <TrendChart
            height={220}
            formatX={formatPeriod}
            formatValue={(v) => formatNumber(v)}
            series={[
              { key: 'credits', label: 'Consumed', color: 'var(--series-1)', points: trend.map((p) => ({ date: p.period, value: microToDisplayed(p.credits, scale) ?? p.credits })) },
              { key: 'cost', label: 'Our cost', color: 'var(--series-2)', points: trend.map((p) => ({ date: p.period, value: microToDisplayed(p.cost_credits, scale) ?? p.cost_credits })) },
            ]}
          />
          <details className="viz-table">
            <summary>Table</summary>
            <table className="table">
              <thead><tr><th>Month</th><th className="num">Credits</th><th className="num">Cost</th><th className="num">Margin</th></tr></thead>
              <tbody>
                {trend.map((p) => (
                  <tr key={p.period}>
                    <td>{formatPeriod(p.period)}</td>
                    <td className="num">{formatCredits(p.credits, scale)}</td>
                    <td className="num">{formatCredits(p.cost_credits, scale)}</td>
                    <td className="num">{formatCredits(p.margin_credits, scale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </Section>
      </div>
    </>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card stat-tile">
      <div className="stat-label t-label">{label}</div>
      <div className="t-display tabular">{value}</div>
      {sub && <div className="stat-sub t-caption">{sub}</div>}
    </div>
  );
}
